import { useUserIdentityContext } from "@core/services/user-identity.context";
import {
  EarnResponse,
  GetRewardModel,
  MutateResponseRewardModel,
} from "@factory/model/query-model-reward";
import { useQyGetBranch } from "@factory/query/branch.query";
import {
  useQyEarnReward,
  useQyGetConversion,
} from "@factory/query/reward.query";
import { useQyGetVehicle, useQyGetVehicleByCardSeries } from "@factory/query/vehicle.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { LabelValue } from "@shared/models/label-value.interface";
import HeaderContent from "@shared/ui/header-content/header-content";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import Scan from "@shared/ui/scan/scan";
import { currencyFormat } from "@shared/utility/formats/currency-format";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export interface EarnRewardProps { }

export function EarnReward() {
  const { getUserRole, getClientFeatures } = useUserIdentityContext();
  const { showSuccess } = useNotificationContext();
  const navigate = useNavigate();
  const { mapEarnReward } = useFormApiMapper();
  const [scan, setScan] = useState(false);
  const [amount, setAmount] = useState("");
  const [cardAlias, setCardAlias] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [receiptNumber, setReceiptNumber] = useState("");
  const [conversion, setConversion] = useState<GetRewardModel>();
  const { tenant } = getUserRole();
  const clientFeatures = getClientFeatures();

  const handleSuccess = () => {
    const responseData = response?.data || [];
    if (responseData.length === 0) {
      return;
    }

    setSelectedBranch(responseData?.[0].code);
  };
  const { data: response } = useQyGetBranch(
    "",
    9999,
    0,
    undefined,
    undefined,
    true,
    handleSuccess,
  );
  const branchOption = LabelValue.MapArray(
    response?.data || [],
    (item) => item.name,
    (item) => item.code,
  );

  const handleConversionSuccess = (response: MutateResponseRewardModel) => {
    const data = response as GetRewardModel[];
    if (data?.length === 0) {
      return;
    }
    setConversion(data?.[0]);
  };
  useQyGetConversion(
    cardAlias,
    selectedBranch,
    tenant,
    !conversion ? (cardAlias && selectedBranch ? true : false) : false,
    handleConversionSuccess,
  );

  const { data: vehicleData } = useQyGetVehicleByCardSeries(cardAlias, !!cardAlias);
  const vehicleOption = LabelValue.MapArray(
    vehicleData?.data || [],
    (item) => `${item.make} ${item.model} (${item.plate_number})`,
    (item) => item.code,
  );

  const handleEarnSuccess = (response: MutateResponseRewardModel) => {
    const data = response.data as EarnResponse;
    showSuccess(`Earn Complete`);
    navigate(
      `/business/earn-success/${cardAlias}?points=${data.points}&stamp=${data.stamp}`,
    );
  };
  const { mutate: earn } = useQyEarnReward(handleEarnSuccess);

  const handleEarn = () => {
    const { tenant } = getUserRole();
    const amountValue = parseInt(amount, 10);

    if (!cardAlias) {
      return;
    }

    const value = mapEarnReward(
      cardAlias,
      amountValue,
      selectedBranch,
      tenant,
      couponCode,
      selectedVehicle,
      receiptNumber,
    );
    earn(value);
  };
  const handleSuccessScan = (value: string) => {
    setCardAlias(value);
  };
  const handleCloseScan = () => {
    setScan(false);
  };
  const accept = () => {
    handleEarn();
  };
  const confirmEarn = () => {
    confirmDialog({
      message: (
        <div>
          <div className="mb-4">
            Are you sure you want to continue earning with{" "}
            <b>{currencyFormat(parseInt(amount, 10), "PHP")}</b>?
          </div>
          <div>
            Current Earning Conversion
            <div>
              {conversion?.is_points && (
                <div>
                  {`${currencyFormat(conversion.denomination_points || 0, "PHP")} = ${conversion.points} point`}
                </div>
              )}
              {conversion?.is_stamp && (
                <div>
                  {`${currencyFormat(conversion.denomination_stamp || 0, "PHP")} = ${conversion.stamp} stamp`}
                </div>
              )}
            </div>
          </div>
        </div>
      ),
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "accept",
      accept,
    });
  };

  // LOCAL RENDER
  const footerContent = (
    <div>
      <Button
        className="block w-full mt-4"
        label="Earn"
        onClick={confirmEarn}
        disabled={!conversion}
      />
      <Button
        className="block w-full mt-2"
        label="Close"
        severity="secondary"
        outlined
        onClick={() => navigate("/business/campaign")}
      />
    </div>
  );

  return (
    <div id="EarnReward" className="earn-reward">
      <ConfirmDialog />

      <HeaderContent title={<h1>Earn</h1>}></HeaderContent>
      <div className="p-7">
        {scan ? (
          <Scan
            successScan={handleSuccessScan}
            closeScan={handleCloseScan}
            isCloseOnScan={true}
          />
        ) : null}
        <div className="mt-6"></div>
        <div className="mb-6">
          <div className="p-inputgroup flex-1">
            <FloatLabel>
              <InputText
                id="userCode"
                value={cardAlias}
                onChange={(e) => setCardAlias(e.target.value)}
                keyfilter="int"
                className="text-4xl w-full"
                autoFocus
                tabIndex={0}
              />
              <label htmlFor="amount">Enter Card Number</label>
            </FloatLabel>

            <span
              className="p-inputgroup-addon cursor-pointer px-8 bg-emerald-500 border border-emerald-400"
              onClick={() => setScan(true)}
            >
              <i className="pi pi-camera text-white"></i>
            </span>
          </div>
        </div>
        <div className="mb-6">
          <FloatLabel>
            <InputText
              id="amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              keyfilter="int"
              className="text-4xl w-full"
            />
            <label htmlFor="amount">Enter Amount</label>
          </FloatLabel>
        </div>
        {clientFeatures?.featureReceiptNotes ? (
          <div className="mb-6">
            <FloatLabel>
              <InputText
                id="receiptNumber"
                value={receiptNumber}
                onChange={(e) => setReceiptNumber(e.target.value)}
                className="text-4xl w-full"
              />
              <label htmlFor="receiptNumber">Enter Receipt Number</label>
            </FloatLabel>
          </div>
        ): null}
        <div className="mb-6">
          <Dropdown
            value={selectedBranch}
            onChange={(e) => setSelectedBranch(e.value)}
            options={branchOption}
            placeholder="Select a branch"
            className="w-full md:w-14rem text-4xl"
          />
        </div>
        {clientFeatures?.featureCoupon ? (
          <div className="mb-6">
            <FloatLabel>
              <InputText
                id="couponCode"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                className="text-4xl w-full"
              />
              <label htmlFor="couponCode">Enter Coupon Code (Optional)</label>
            </FloatLabel>
          </div>
        ) : null}
        {clientFeatures?.featureFleetManagement ? (
          <div className="mb-6">
            <Dropdown
              value={selectedVehicle}
              onChange={(e) => setSelectedVehicle(e.value)}
              options={vehicleOption}
              placeholder="Select a Vehicle"
              className="w-full md:w-14rem text-4xl"
            />
          </div>
        ): null}
        {footerContent}
      </div>
    </div>
  );
}

export default EarnReward;
